import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Separate axios instance for file uploads (no default Content-Type)
export const uploadAPI_instance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  // No default Content-Type header
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/molly-butler-lodge/admin';
    }
    return Promise.reject(error);
  }
);

// Add same interceptors to upload API instance
uploadAPI_instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

uploadAPI_instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/molly-butler-lodge/admin';
    }
    return Promise.reject(error);
  }
);

// Room API calls
export const roomsAPI = {
  getAll: () => api.get('/rooms'),
  getById: (id: string) => api.get(`/rooms/${id}`),
  checkAvailability: (roomId: string, checkIn: string, checkOut: string) => 
    api.post('/rooms/check-availability', { roomId, checkIn, checkOut }),
};

// Reservation API calls
export const reservationsAPI = {
  create: (reservationData: any) => api.post('/reservations', reservationData),
  getByConfirmation: (confirmationNumber: string) => 
    api.get(`/reservations/confirmation/${confirmationNumber}`),
  cancel: (confirmationNumber: string) => 
    api.patch(`/reservations/confirmation/${confirmationNumber}`, { status: 'cancelled' }),
  getAll: (filters?: any) => api.get('/reservations', { params: filters }),
  getById: (id: string) => api.get(`/reservations/${id}`),
  update: (id: string, data: any) => api.put(`/reservations/${id}`, data),
  getStats: () => api.get('/reservations/admin/stats'),
};

// Events API calls
export const eventsAPI = {
  getAll: () => api.get('/events'),
  getAllForMonth: (year: number, month: number) => api.get('/events', { params: { year, month } }),
  getAllAdmin: () => api.get('/events/admin/all'),
  getById: (id: string) => api.get(`/events/${id}`),
  create: (eventData: any) => api.post('/events', eventData),
  update: (id: string, eventData: any) => api.put(`/events/${id}`, eventData),
  delete: (id: string) => api.delete(`/events/${id}`),
  cancel: (id: string) => api.patch(`/events/${id}/cancel`),
  pause: (id: string) => api.patch(`/events/${id}/pause`),
  resume: (id: string) => api.patch(`/events/${id}/resume`),
  createWithFile: (formData: FormData) => api.post('/events', formData),
  updateWithFile: (id: string, formData: FormData) => api.put(`/events/${id}`, formData),
};

// Upload API calls
export const uploadAPI = {
  uploadImage: (file: File, folder?: string) => {
    const formData = new FormData();
    formData.append('image', file);

    const url = folder ? `/upload/image?folder=${folder}` : '/upload/image';
    return uploadAPI_instance.post(url, formData);
  },
  uploadImages: (files: File[], folder?: string) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });

    const url = folder ? `/upload/images?folder=${folder}` : '/upload/images';
    return uploadAPI_instance.post(url, formData);
  },
  deleteImage: (imageUrl: string) => api.delete('/upload/image', { data: { imageUrl } }),
};

// Auth API calls
export const authAPI = {
  login: (credentials: { username: string; password: string }) =>
    api.post('/auth/login', credentials),
  register: (userData: any) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
};

// User API calls
export const userAPI = {
  getMe: () => api.get('/users/me'),
  updateProfile: (userData: { username?: string; email?: string; firstName?: string; lastName?: string }) =>
    api.put('/users/profile', userData),
  changePassword: (passwords: { currentPassword: string; newPassword: string }) =>
    api.post('/users/change-password', passwords),
};

export default api;